<template>
  <div class="mt-adhesion__tarjeta">
    <div v-if="enviado" class="mt-adhesion__hecho" role="status">
      <v-icon size="40">mdi-check-circle-outline</v-icon>
      <h3>Solicitud recibida</h3>
      <p>Gracias. Nos pondremos en contacto con su notaría para darla de alta.</p>
    </div>
    <form v-else novalidate @submit.prevent="enviar">
      <h3>Adherir mi notaría</h3>
      <p class="mt-adhesion__nota">Solo cuatro datos. Nosotros le contactamos.</p>

      <v-text-field v-model="f.notario" label="Nombre del notario o notaria" variant="outlined" density="comfortable"
        maxlength="150" autocomplete="name" :error-messages="err('NOTARIO')" />
      <v-select v-model="f.casaInformatica" :items="CASAS" item-title="texto" item-value="valor" label="Casa informática"
        variant="outlined" density="comfortable" :error-messages="err('CASA')" />
      <v-text-field v-if="f.casaInformatica === 'OTRA'" v-model="f.otraCasa" label="¿Cuál?" variant="outlined" density="comfortable"
        maxlength="80" :error-messages="err('OTRA_CASA')" />
      <v-text-field v-model="f.web" label="Web de la notaría (si tiene)" variant="outlined" density="comfortable"
        maxlength="200" autocomplete="url" placeholder="https://" />
      <v-text-field v-model="f.contacto" label="Teléfono o email de contacto" variant="outlined" density="comfortable"
        maxlength="200" :error-messages="err('CONTACTO')" />

      <!-- Honeypot: los bots lo rellenan -->
      <input v-model="f.website" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" class="mt-adhesion__trampa">

      <v-checkbox v-model="f.consentimiento" density="compact" hide-details="auto" :error-messages="err('CONSENTIMIENTO')">
        <template #label>
          <span class="mt-adhesion__acepto">Acepto que Unidata use estos datos para gestionar la adhesión, según la
            <NuxtLink :to="rutaPrivacidad" target="_blank" @click.stop>política de protección de datos</NuxtLink>.</span>
        </template>
      </v-checkbox>

      <p v-if="fallo" class="mt-adhesion__error" role="alert">{{ fallo }}</p>
      <button type="submit" class="mt-boton mt-boton--acento mt-boton--grande mt-adhesion__boton" :disabled="enviando">
        {{ enviando ? 'Enviando…' : 'Quiero adherir mi notaría' }} <v-icon v-if="!enviando" size="20">mdi-arrow-right</v-icon>
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useStore } from '../../store/index'
import { apiSolicitudes } from '../../composables/useSolicitudWeb'

// "Adherir mi notaría": va al correo de contacto del portal en el panel y a Respuestas del formulario.
// Ref: https://trello.com/c/8j6VOWgP
const CASAS = [
  { valor: 'NOTIN', texto: 'Notin' }, { valor: 'AGN', texto: 'AGN' }, { valor: 'JPA', texto: 'JPA' }, { valor: 'MTC', texto: 'MTC' },
  { valor: 'NOTARNET', texto: 'Notarnet' }, { valor: 'WORDPLEX', texto: 'Wordplex' }, { valor: 'OTRA', texto: 'Otra' },
]
const store = useStore()
const router = useRouter()
const rutaPrivacidad = computed(() => (router.currentRoute.value.params.lang ? `/${router.currentRoute.value.params.lang}` : '') + '/ProteccionDatos')

const f = reactive({ notario: '', casaInformatica: null as string | null, otraCasa: '', web: '', contacto: '', consentimiento: false, website: '' })
const errores = ref<string[]>([])
const fallo = ref('')
const enviando = ref(false)
const enviado = ref(false)

const MENSAJES: Record<string, string> = {
  NOTARIO: 'Indique el nombre del notario o notaria.',
  CASA: 'Elija la casa informática.',
  OTRA_CASA: 'Indique cuál.',
  CONTACTO: 'Indique un teléfono o un email válido.',
  CONSENTIMIENTO: 'Necesitamos su consentimiento para tramitar la solicitud.',
}
const err = (codigo: string) => (errores.value.includes(codigo) ? [MENSAJES[codigo]] : [])

function validar(): string[] {
  const e: string[] = []
  if (!f.notario.trim()) e.push('NOTARIO')
  if (!f.casaInformatica) e.push('CASA')
  else if (f.casaInformatica === 'OTRA' && !f.otraCasa.trim()) e.push('OTRA_CASA')
  const contacto = f.contacto.trim()
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contacto) && contacto.replace(/\D/g, '').length < 9) e.push('CONTACTO')
  if (!f.consentimiento) e.push('CONSENTIMIENTO')
  return e
}

async function enviar() {
  fallo.value = ''
  errores.value = validar()
  if (errores.value.length) return
  enviando.value = true
  try {
    const n: any = store.NOTARIA
    await $fetch(`${apiSolicitudes(store)}api/ContactoWeb/Minotaria/${n?.id ?? n?.IdValue}/adhesion`, { method: 'POST', body: { ...f } })
    enviado.value = true
  } catch (e: any) {
    const codigo = e?.data?.codigo
    if (codigo && MENSAJES[codigo]) errores.value = [codigo]
    else fallo.value = 'No se ha podido enviar. Inténtelo de nuevo en unos minutos.'
  } finally {
    enviando.value = false
  }
}
</script>
